/**
 * Utilidades para normalizar respuestas de sync HomeCenter (manual cron)
 */
import type {
  SyncByDateDetail,
  SyncByDateResponse,
} from '../types/cron/homecenterManual'

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
}

/**
 * Convierte detalles/details a array aunque vengan omitidos, null, objeto o en inglés
 */
export function coerceHomeCenterDetalles(value: unknown): SyncByDateDetail[] {
  if (value == null) {
    return []
  }
  if (Array.isArray(value)) {
    return value as unknown as SyncByDateDetail[]
  }
  if (isRecord(value)) {
    return Object.values(value).filter(isRecord) as unknown as SyncByDateDetail[]
  }
  return []
}

function pickNumber(raw: Record<string, unknown>, key: string): number {
  const n = Number(raw[key])
  return Number.isFinite(n) ? n : 0
}

/**
 * Normaliza cualquier forma de respuesta del backend a SyncByDateResponse
 */
export function normalizeHomeCenterSyncByDateResponse(
  data: unknown,
  fallbackFecha: string
): SyncByDateResponse {
  const payload = isRecord(data) ? data : {}
  const dataNode = isRecord(payload.data) ? payload.data : null

  let raw: Record<string, unknown>

  const resultsNode = payload.results ?? payload.result ?? dataNode?.results ?? dataNode?.result

  if (Array.isArray(resultsNode)) {
    raw = {
      fecha: fallbackFecha,
      detalles: resultsNode,
      total: resultsNode.length,
    }
  } else if (isRecord(resultsNode)) {
    raw = resultsNode
  } else if (isRecord(dataNode)) {
    raw = dataNode
  } else {
    raw = payload
  }

  const detalles = coerceHomeCenterDetalles(
    raw.detalles ?? raw.details ?? payload.detalles ?? payload.details
  )

  const fecha =
    typeof raw.fecha === 'string'
      ? raw.fecha
      : typeof payload.fecha === 'string'
        ? payload.fecha
        : fallbackFecha

  return {
    success: Boolean(payload.success ?? dataNode?.success ?? true),
    message:
      typeof payload.message === 'string'
        ? payload.message
        : typeof dataNode?.message === 'string'
          ? (dataNode.message as string)
          : '',
    results: {
      fecha,
      total: pickNumber(raw, 'total') || pickNumber(payload, 'total') || detalles.length,
      created: pickNumber(raw, 'created') || pickNumber(payload, 'created'),
      skipped: pickNumber(raw, 'skipped') || pickNumber(payload, 'skipped'),
      errors: pickNumber(raw, 'errors') || pickNumber(payload, 'errors'),
      detalles,
    },
  }
}

/**
 * Suma segura de detalles para consolidación (nunca hace spread de undefined)
 */
export function appendHomeCenterDetalles(
  target: SyncByDateDetail[],
  resultsBlock: SyncByDateResponse['results'] | null | undefined
): void {
  if (!resultsBlock) {
    return
  }
  target.push(...coerceHomeCenterDetalles(resultsBlock.detalles))
}
